/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Component, MouseEvent } from 'react';
import { Card } from 'antd';
import { Equipment, _Equipment } from '../../_shared/interfaces/equipment';
import { cardStyles as styles } from './manage-equipment.styles';
import SharedStyles from '../../_shared/styles';
import colors from '../../_shared/colors';
import { Org } from '../../_shared/interfaces/org';
import { Location } from '../../_shared/interfaces/location';
import { renderLabel } from '../../utils/mixins';
import { get, last, sortBy } from 'lodash';
import { Model } from '../../_shared/interfaces/model';
import ModelImage from '../../_shared/model-image';
import { Publish } from '../../_shared/interfaces/publish';
import {
  renderStatus,
  RenderAlert,
  RenderConsignment,
} from './renders.component.';
import { AppState } from '../../app.state';
import {
  isDentalOrgAdmin,
  isServiceOrgAdmin,
  isPB,
  getEquipMaint,
  getTriggeredMaint,
  handpieceIsDueForService,
  isHandpiece,
  userHasRole,
  isServiceOrgUser,
  isDentalOrgUser,
  findWorstError,
  errorDef,
} from '../../utils';
import { AIcon as Icon } from '../../_shared/icon';
import {
  EQStatus,
  MaintStatus,
} from '../../_shared/interfaces/equipmentStatus';

interface IProps {
  equipment: Equipment;
  onClick: () => void;
  onEditClick: () => void;
  location?: Location;
  serviceOrg?: Org;
  dentalOrg?: Org;
  model?: Model;
  config?: Publish;
  alerts: Publish[];
  alertMap: {};
  loading: boolean;
  sState: AppState;
}

export class EquipCard extends Component<IProps> {
  static defaultProps: IProps;
  editClick = (e: MouseEvent) => {
    const { onEditClick } = this.props;
    e.preventDefault();
    e.stopPropagation();

    onEditClick && onEditClick();
  };
  onClear = async () => {
    this.setState({ modalVisible: false });
  };
  render() {
    const {
      equipment,
      onClick,
      serviceOrg,
      dentalOrg,
      location,
      model,
      alertMap,
      loading,
      sState,
    } = this.props;

    const eq = new _Equipment(equipment);
    const isDOAdmin = isDentalOrgAdmin(sState);
    const isSOAdmin = isServiceOrgAdmin(sState);
    // AER-1597: match Equipment Details sidebar gating — Org Read Only (6)
    // and DentalEZ Read Only (7) shouldn't see the Edit pencil.
    const userIsOrgReadOnly = userHasRole([6], sState);
    const userIsDezReadOnly = userHasRole([7], sState);
    const showEdit =
      !equipment.isSmartChair && !userIsOrgReadOnly && !userIsDezReadOnly;

    const ispb = isPB(sState);

    var isServiceDue = false;
    var isServiceOverDue = false;
    var timeUntilServiceDueHours = -1;

    if (isHandpiece(equipment) === false) {
      const maint = getEquipMaint(eq, sState, []);
      let timeToService = getTriggeredMaint(maint[0]);

      timeToService.length &&
        timeToService.sort((a, b) => {
          if (a.timeRemaining > b.timeRemaining) return 1;
          if (a.timeRemaining < b.timeRemaining) return -1;
          return 0;
        });
      isServiceDue = !!timeToService.length;
      timeUntilServiceDueHours =
        timeToService.length > 0 ? timeToService[0].timeRemaining : -1;
      isServiceOverDue =
        !!timeToService.length && timeToService[0].timeRemaining < 0;
    } else {
      const [due, hours] = handpieceIsDueForService(sState, equipment, []);
      isServiceDue = due;
      timeUntilServiceDueHours = 500 - hours;
      isServiceOverDue = timeUntilServiceDueHours < 0;
    }

    const modelValue = get(
      model,
      'name',
      get(model, 'id', 'Osprey Compressor')
    );
    const locationValue = get(location, 'name');
    const dentalOrgValue = get(dentalOrg, 'name');

    if (ispb) {
      // mobile: Model line should display the Model ID (e.g. C6-2 / RV7-2 /
      // 6400 / E900) — not the human-readable name.
      const mobileModelValue = get(model, 'displayName', '');

      // mobile: equipment Type label below the SN line. Map model.type to a
      // simple label (Compressor / Vacuum / Chair / Handpiece / Sterilizer);
      // 'aerasone_*' variants collapse to the base type.
      const typeMap: { [key: string]: string } = {
        compressor: 'Compressor',
        aerasone_compressor: 'Compressor',
        vacuum: 'Vacuum',
        aerasone_vacuum: 'Vacuum',
        chair: 'Chair',
        handpiece: 'Handpiece',
        sterilizer: 'Sterilizer',
      };
      const typeValue = typeMap[get(model, 'type', '') as string] || '';

      // mobile: top-of-card stripe matching the alert triangle color, but only
      // when the worst alert is a major or minor (not informational).
      const worstAlert = findWorstError(
        get(equipment, 'status.alerts') as unknown as EQStatus['alerts'],
        equipment
      );
      const alertCode = worstAlert
        ? errorDef(get(worstAlert, 'error', 0))
        : null;
      const alertStripeColor =
        alertCode && (alertCode.isMajor || alertCode.isMinor)
          ? alertCode.color
          : null;
      // Service Due: use status.maint (same source as web app table view)
      // so that it actually displays when there's data.
      const sortedMaint = sortBy(
        get(equipment, 'status.maint', []) as MaintStatus[],
        'timeDiff'
      );
      const mobileIsServiceDue = !!sortedMaint.find(m => m.timeDiff > 0);
      const mobileIsServiceOverDue = !!sortedMaint.find(m => m.timeDiff <= 0);
      const mobileTimeUntilServiceDueHours = get(last(sortedMaint), 'timeDiff');

      // Role-based Dental Org / Location display:
      // - SuperAdmin (0), TechAdmin (1), DentalEZ Read Only (7): show both.
      // - Org users (2, 3, 4, 6): SO orgs show both, DO orgs show neither.
      const isDentalEzInternal = userHasRole([0, 1, 7], sState);
      const isSOUser = isServiceOrgUser(sState);
      // AER-1698: role 6 (Org Read Only) is now covered by both isServiceOrgUser and
      // isDentalOrgUser, so isSOUser on its own handles SO-affiliated read-only users.
      // The org-type fallback below is kept for org users whose org can't be resolved
      // at all (no orgId — see table-header.component.tsx — or an org missing from
      // dash.orgs): both helpers return false there and we still want to show the
      // Dental Org / Location lines rather than silently hide them.
      const isOrgUserRole = userHasRole([2, 3, 4, 6], sState);
      const isDOUser = isDentalOrgUser(sState);
      const showOrgAndLocation =
        isDentalEzInternal ||
        (isOrgUserRole && isSOUser) ||
        (isOrgUserRole && !isDOUser && !isDOAdmin);
      const hideOrgAndLocation = isOrgUserRole && (isDOUser || isDOAdmin);

      return (
        <div
          css={css(
            styles.container,
            'width: 100%; padding: 0; margin-bottom: 10px;'
          )}
        >
          <Card
            onClick={onClick}
            css={css(
              styles.mobileCard,
              eq.alert > 0 && !alertStripeColor && styles.cardOutline,
              'height: auto;'
            )}
            hoverable
          >
            {alertStripeColor && (
              <div
                css={css(styles.mobileAlertStripe)}
                style={{ backgroundColor: alertStripeColor }}
              />
            )}
            <div
              css={css(
                styles.mobileStatusRight,
                alertStripeColor && styles.mobileStatusRightWithStripe
              )}
            >
              {renderStatus({ equipment, loading, sState })}
            </div>
            <div css={css(styles.mobileIdentifier)}>
              <p css={css(styles.mobileIdentifierText)}>{eq.name || 'None'}</p>
              <RenderAlert equipment={equipment} />
              <RenderConsignment equipment={equipment} />
            </div>
            <div css={css(styles.mobileDetails)}>
              {!!typeValue && (
                <p css={css(styles.mobileDetailLine)}>
                  <span css={css(styles.mobileLabel)}>Type: </span>
                  <span css={css(styles.mobileValue)}>{typeValue}</span>
                </p>
              )}
              <p css={css(styles.mobileDetailLine)}>
                <span css={css(styles.mobileLabel)}>Model: </span>
                <span css={css(styles.mobileValue)}>{mobileModelValue}</span>
              </p>
              {!hideOrgAndLocation && showOrgAndLocation && !!dentalOrgValue && (
                <p css={css(styles.mobileDetailLine)}>
                  <span css={css(styles.mobileLabel)}>Dental Org: </span>
                  <span css={css(styles.mobileValue)}>{dentalOrgValue}</span>
                </p>
              )}
              {!hideOrgAndLocation && !!locationValue && (
                <p css={css(styles.mobileDetailLine)}>
                  <span css={css(styles.mobileLabel)}>Location: </span>
                  <span css={css(styles.mobileValue)}>{locationValue}</span>
                </p>
              )}
              {mobileIsServiceOverDue ? (
                <p
                  css={css(styles.serviceOverdueText, styles.mobileDetailLine)}
                >
                  Service Overdue
                </p>
              ) : mobileIsServiceDue && (mobileTimeUntilServiceDueHours || 0) > 0 ? (
                <p css={css(styles.mobileDetailLine)}>
                  <span css={css(styles.mobileLabel)}>Service Due: </span>
                  <span
                    css={css(styles.mobileValue)}
                  >{`${mobileTimeUntilServiceDueHours} hours`}</span>
                </p>
              ) : null}
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div css={css(styles.container)}>
        <Card
          onClick={onClick}
          css={css(styles.card, eq.alert > 0 && styles.cardOutline)}
          hoverable
          cover={
            <div css={css(SharedStyles.row, styles.imageHolder)}>
              {showEdit && (
                <Icon
                  onClick={this.editClick}
                  type={'edit'}
                  css={css(styles.icon, `color: ${colors.highlight}`)}
                />
              )}
              <ModelImage css={css(styles.image)} model={model} />
            </div>
          }
        >
          <div>
            <div css={css(styles.cardText)}>
              <div css={css(SharedStyles.row)}>
                {renderLabel('name', {
                  styles: styles.cardTitle,
                  get: val => val || 'None',
                  obj: eq,
                })}
                <RenderAlert equipment={equipment} />
                <RenderConsignment equipment={equipment} />
              </div>
              <p>&nbsp;</p>
              {renderLabel('modelName', {
                obj: eq,
                prefix: 'Model: ',
                get: _val => modelValue,
              })}
              {renderLabel('locationId', {
                obj: eq,
                prefix: 'Location: ',
                get: (_val: string) => locationValue,
              })}
              {!isDOAdmin &&
                !isSOAdmin &&
                renderLabel('serviceOrgId', {
                  obj: eq,
                  prefix: 'Service Org: ',
                  get: (_val: string) => get(serviceOrg, 'name'),
                })}
              {!isDOAdmin &&
                renderLabel('dentalOrgId', {
                  obj: eq,
                  prefix: 'Dental Org: ',
                  get: (_val: string) => get(dentalOrg, 'name'),
                })}
              {isServiceOverDue ? (
                <p css={css(styles.serviceOverdueText)}>Service Overdue</p>
              ) : isServiceDue && timeUntilServiceDueHours > 0 ? (
                <p>{`Service Due: ${timeUntilServiceDueHours} hours`}</p>
              ) : (
                <br />
              )}
            </div>

            {renderStatus({ equipment, loading, sState })}
          </div>
        </Card>
      </div>
    );
  }
}

EquipCard.defaultProps = {
  equipment: {} as Equipment,
  onClick: () => 0,
  onEditClick: () => 0,
  locations: {},
  orgs: {},
  alerts: [] as Publish[],
  alertMap: {},
  loading: true,
  sState: {} as AppState,
} as IProps;
